/**
 * LEAN THEME - Free Shipping Meter
 * Live-updates all shipping meter instances when the cart changes.
 * Listens to cart:updated and cart:item-added events dispatched by cart.js.
 */
'use strict';

const LeanShippingMeter = (() => {
  const SELECTOR = '[data-shipping-meter]';
  let hasReachedBefore = false;

  /**
   * Format money using Shopify's formatter or fallback
   */
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    if (window.LeanTheme?.formatMoney) return window.LeanTheme.formatMoney(cents);
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
  };

  /**
   * Update all meter instances on the page
   */
  const update = (totalPrice) => {
    document.querySelectorAll(SELECTOR).forEach((meter) => {
      const threshold = parseInt(meter.dataset.threshold) || 0;
      if (threshold === 0) return;

      const barColor = meter.dataset.barColor || '#000000';
      const successColor = meter.dataset.successColor || '#22c55e';
      const msgRemaining = meter.dataset.msgRemaining || '';
      const msgAlmost = meter.dataset.msgAlmost || '';
      const msgReached = meter.dataset.msgReached || '';

      const remaining = Math.max(0, threshold - totalPrice);
      const percent = Math.min((totalPrice / threshold) * 100, 100);
      const isReached = remaining <= 0;
      const isAlmost = percent >= 90 && !isReached;

      // Update bar width
      const bar = meter.querySelector('[data-shipping-meter-bar]');
      if (bar) {
        bar.style.width = percent + '%';
      }

      // Update state classes
      meter.classList.toggle('shipping-meter--reached', isReached);
      meter.classList.toggle('shipping-meter--almost', isAlmost);

      // Update bar color dynamically
      if (isReached) {
        meter.style.setProperty('--meter-success-color', successColor);
      } else {
        meter.style.setProperty('--meter-bar-color', barColor);
      }

      // Update progressbar ARIA
      const track = meter.querySelector('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', Math.round(percent));
      }

      // Update message text
      const textEl = meter.querySelector('[data-shipping-meter-text]');
      if (textEl) {
        textEl.innerHTML = buildMessage({
          isReached,
          isAlmost,
          remaining,
          msgRemaining,
          msgAlmost,
          msgReached
        });
      }

      // Show/hide meter based on cart state
      // The Section Rendering API will handle hiding when cart is empty,
      // but for JS-only updates we toggle visibility
      meter.hidden = false;

      // Celebration on first reach
      const celebrateEnabled = meter.dataset.celebrate !== 'false';
      const celebrateType = meter.dataset.celebrateType || 'confetti';
      if (isReached && !hasReachedBefore) {
        hasReachedBefore = true;
        if (celebrateEnabled) celebrate(meter, celebrateType);
      } else if (!isReached) {
        hasReachedBefore = false;
      }
    });
  };

  /**
   * Build the message HTML for the current state
   */
  const buildMessage = ({ isReached, isAlmost, remaining, msgRemaining, msgAlmost, msgReached }) => {
    const checkIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>';
    const truckIcon = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';

    if (isReached) {
      const text = msgReached || 'Du erh\u00E4ltst kostenlosen Versand!';
      return `<span class="shipping-meter__icon" aria-hidden="true">${checkIcon}</span><span class="shipping-meter__text">${text}</span>`;
    }

    const icon = `<span class="shipping-meter__icon" aria-hidden="true">${truckIcon}</span>`;
    const amount = formatMoney(remaining);

    if (isAlmost) {
      if (msgAlmost) {
        const text = msgAlmost.replace('[amount]', amount);
        return `${icon}<span class="shipping-meter__text">${text}</span>`;
      }
      return `${icon}<span class="shipping-meter__text">Fast geschafft! Nur noch <strong>${amount}</strong>!</span>`;
    }

    if (msgRemaining) {
      const text = msgRemaining.replace('[amount]', amount);
      return `${icon}<span class="shipping-meter__text">${text}</span>`;
    }
    return `${icon}<span class="shipping-meter__text">Nur noch <strong>${amount}</strong> bis zum kostenlosen Versand!</span>`;
  };

  /**
   * Celebration animation when threshold is reached.
   * Supports 'pulse' (glow) and 'confetti' types.
   */
  const celebrate = (meter, type) => {
    const cls = type === 'pulse' ? 'shipping-meter--celebrate-pulse' : 'shipping-meter--celebrate-confetti';
    meter.classList.add(cls);
    setTimeout(() => {
      meter.classList.remove(cls);
    }, 1500);
  };

  /**
   * Fetch current cart and update meters
   */
  const refresh = async () => {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await res.json();
      update(cart.total_price);
    } catch (err) {
      console.error('Shipping meter refresh error:', err);
    }
  };

  /**
   * Bind to cart events
   */
  const bindEvents = () => {
    // Listen to events from LeanCart
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:item-added', refresh);

    // Also listen to a direct update event with total price
    document.addEventListener('shipping-meter:update', (e) => {
      if (e.detail?.totalPrice !== null && e.detail?.totalPrice !== undefined) {
        update(e.detail.totalPrice);
      }
    });
  };

  const init = () => {
    // Only init if meters exist
    if (!document.querySelector(SELECTOR)) return;

    bindEvents();

    // Check initial state for hasReachedBefore
    document.querySelectorAll(SELECTOR).forEach((meter) => {
      if (meter.classList.contains('shipping-meter--reached')) {
        hasReachedBefore = true;
      }
    });
  };

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init, update, refresh };
})();

window.LeanShippingMeter = LeanShippingMeter;
